import { useQuery } from '@tanstack/react-query';
import { statsApi } from '@/api/stats.api';

/**
 * Public platform figures (GET /stats/platform, cached 5 min server side).
 * One query key for the whole app, so the home page, the auth screens and
 * the protected-route banner share one request and show the same numbers.
 */
export function usePlatformStats() {
  return useQuery({
    queryKey: ['stats', 'platform'],
    queryFn: () => statsApi.getPlatform(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** 12 345 -> "12.3k", 980 -> "980". */
export function formatCompactCount(value: number): string {
  if (value >= 1000) {
    const thousands = Math.round((value / 1000) * 10) / 10;
    return `${thousands}k`;
  }
  return value.toLocaleString();
}

/**
 * The live figures of the auth screens. They used to be hardcoded ("12"
 * languages, "600k+"), which never moved and matched no other screen.
 * `null` while loading or when the request fails: callers show a dash.
 */
export function useAuthPlatformFigures() {
  const { data } = usePlatformStats();
  return {
    languages: data ? String(data.languages.active) : null,
    collectedAudios: data ? formatCompactCount(data.audio.total) : null,
    registeredUsers: data ? data.contributors.total : null,
  };
}
